// src/admin/ReferralSystemSettings.jsx
// Admin Settings for Referral System Configuration

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../config/firebase-config';
import { 
  Users, Calendar, Clock, DollarSign, Bell, 
  Settings, Save, RotateCcw, Check, Info,
  AlertTriangle, Shield, Mail, Smartphone
} from 'lucide-react';

const ReferralSystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const functions = getFunctions();

  // Default settings structure
  const defaultSettings = {
    // Core Business Rules
    coreRules: {
      minDaysBeforeRemoval: 30,
      referralDurationDays: 365, // 12 months
      extensionDurationDays: 180, // 6 months
      maxExtensions: 1,
      commissionPercentage: 2.0,
      gracePeriodDays: 7 // Days before expiration to show extension option
    },

    // Notification Settings
    notifications: {
      expirationWarningDays: 7,
      enableEmailNotifications: true,
      enableDashboardNotifications: true,
      enableSMSNotifications: false,
      reminderIntervalDays: 1, // How often to remind if not acted upon
      maxReminders: 3
    },

    // System Behavior
    systemBehavior: {
      autoExpireIfNoAction: true,
      allowMultipleRemovalReasons: true,
      requireRemovalReason: false,
      allowMessageToReferrer: true,
      trackRemovalHistory: true,
      enableReferrerNotificationOnRemoval: false
    },

    // Business Protection
    antiAbuse: {
      enableFraudDetection: true,
      maxReferrerChangesPerYear: 2,
      minEarningsBeforeRemoval: 0, // Minimum $ earned before can remove referrer
      coolingOffPeriodDays: 0, // Days before can accept new referrer after removal
      flagSuspiciousPatterns: true
    },

    // Financial Settings
    financial: {
      minPayoutThreshold: 10.00, // Minimum before referrer gets paid
      payoutFrequency: 'weekly', // weekly, monthly, immediate
      referrerEarningsCapPerMonth: 0, // 0 = unlimited
      platformAbsorbsCommission: true // true = comes from platform, false = deducted from musician
    },

    // Special Programs
    specialPrograms: {
      enableLoyaltyBonuses: false,
      loyaltyBonusAfterMonths: 12,
      loyaltyBonusPercentage: 0.5, // Extra % for long-term referrals
      enableSeasonalExtensions: false,
      seasonalExtensionDays: 90
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/getReferralSystemSettingsHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSettings(result.settings || defaultSettings);
      } else {
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading referral settings:', error);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (category, field, value) => {
    const newSettings = { ...settings };
    if (category) {
      newSettings[category][field] = value;
    } else {
      newSettings[field] = value;
    }
    setSettings(newSettings);
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const idToken = await auth.currentUser?.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/updateReferralSystemSettingsHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ settings })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSaveSuccess(true);
        setHasChanges(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all referral settings to defaults? This cannot be undone.')) {
      setSettings(defaultSettings);
      setHasChanges(true);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Users className="animate-spin" size={48} style={{ margin: '0 auto', color: '#7C3AED' }} />
        <p style={{ marginTop: '20px', color: '#6B7280' }}>Loading referral system settings...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: 'white', 
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Users size={32} />
            Referral System Settings
          </h2>
          <p style={{ color: '#9CA3AF', margin: 0 }}>
            Configure how the referral system operates platform-wide
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          {saveSuccess && (
            <div style={{
              backgroundColor: '#10B981',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Check size={20} />
              Saved!
            </div>
          )}
          
          <button
            onClick={resetToDefaults}
            style={{
              backgroundColor: '#6B7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <RotateCcw size={18} />
            Reset to Defaults
          </button>
          
          <button
            onClick={saveSettings}
            disabled={!hasChanges || saving}
            style={{
              backgroundColor: hasChanges ? '#7C3AED' : '#4B5563',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: saving ? 0.7 : 1
            }}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Settings Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
        
        {/* Core Business Rules */}
        <div style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '24px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Settings size={24} />
            Core Business Rules
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SettingInput
              label="Days Before Can Remove Referrer"
              value={settings.coreRules.minDaysBeforeRemoval}
              onChange={(val) => updateSetting('coreRules', 'minDaysBeforeRemoval', val)}
              type="number"
              min={1}
              max={365}
              help="Minimum days after account creation before musician can remove referrer"
            />
            
            <SettingInput
              label="Referral Duration (Days)"
              value={settings.coreRules.referralDurationDays}
              onChange={(val) => updateSetting('coreRules', 'referralDurationDays', val)}
              type="number"
              min={30}
              max={1095}
              help="How long referral relationships last (365 = 12 months)"
            />
            
            <SettingInput
              label="Extension Duration (Days)"
              value={settings.coreRules.extensionDurationDays}
              onChange={(val) => updateSetting('coreRules', 'extensionDurationDays', val)}
              type="number"
              min={30}
              max={365}
              help="How many days to add when extending (180 = 6 months)"
            />
            
            <SettingInput
              label="Maximum Extensions Allowed"
              value={settings.coreRules.maxExtensions}
              onChange={(val) => updateSetting('coreRules', 'maxExtensions', val)}
              type="number"
              min={0}
              max={5}
              help="How many times a referral can be extended"
            />
            
            <SettingInput
              label="Commission Percentage"
              value={settings.coreRules.commissionPercentage}
              onChange={(val) => updateSetting('coreRules', 'commissionPercentage', val)}
              type="number"
              min={0.1}
              max={10}
              step={0.1}
              help="Percentage of earnings that referrers receive"
            />
            
            <SettingInput
              label="Grace Period (Days)"
              value={settings.coreRules.gracePeriodDays}
              onChange={(val) => updateSetting('coreRules', 'gracePeriodDays', val)}
              type="number"
              min={1}
              max={30}
              help="Days before expiration when extension option appears"
            />
          </div>
        </div>

        {/* Notification Settings */}
        <div style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '24px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Bell size={24} />
            Notification Settings
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SettingInput
              label="Expiration Warning (Days)"
              value={settings.notifications.expirationWarningDays}
              onChange={(val) => updateSetting('notifications', 'expirationWarningDays', val)}
              type="number"
              min={1}
              max={30}
              help="Days before expiration to send warning notifications"
            />
            
            <SettingToggle
              label="Email Notifications"
              value={settings.notifications.enableEmailNotifications}
              onChange={(val) => updateSetting('notifications', 'enableEmailNotifications', val)}
              help="Send email notifications for expiration warnings"
              icon={<Mail size={16} />}
            />
            
            <SettingToggle
              label="Dashboard Notifications"
              value={settings.notifications.enableDashboardNotifications}
              onChange={(val) => updateSetting('notifications', 'enableDashboardNotifications', val)}
              help="Show notifications in musician dashboard"
            />
            
            <SettingToggle
              label="SMS Notifications"
              value={settings.notifications.enableSMSNotifications}
              onChange={(val) => updateSetting('notifications', 'enableSMSNotifications', val)}
              help="Send SMS notifications for critical referral events"
              icon={<Smartphone size={16} />}
            />
            
            <SettingInput
              label="Reminder Interval (Days)"
              value={settings.notifications.reminderIntervalDays}
              onChange={(val) => updateSetting('notifications', 'reminderIntervalDays', val)}
              type="number"
              min={1}
              max={7}
              help="Days between repeated reminder notifications"
            />
            
            <SettingInput
              label="Maximum Reminders"
              value={settings.notifications.maxReminders}
              onChange={(val) => updateSetting('notifications', 'maxReminders', val)}
              type="number"
              min={1}
              max={10}
              help="Maximum number of reminder notifications to send"
            />
          </div>
        </div>

        {/* System Behavior */}
        <div style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '24px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Shield size={24} />
            System Behavior
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SettingToggle
              label="Auto-Expire If No Action"
              value={settings.systemBehavior.autoExpireIfNoAction}
              onChange={(val) => updateSetting('systemBehavior', 'autoExpireIfNoAction', val)}
              help="Automatically expire referrals if musician doesn't extend"
            />
            
            <SettingToggle
              label="Require Removal Reason"
              value={settings.systemBehavior.requireRemovalReason}
              onChange={(val) => updateSetting('systemBehavior', 'requireRemovalReason', val)}
              help="Force musicians to select a reason when removing referrer"
            />
            
            <SettingToggle
              label="Allow Message to Referrer"
              value={settings.systemBehavior.allowMessageToReferrer}
              onChange={(val) => updateSetting('systemBehavior', 'allowMessageToReferrer', val)}
              help="Allow musicians to send message when removing referrer"
            />
            
            <SettingToggle
              label="Track Removal History"
              value={settings.systemBehavior.trackRemovalHistory}
              onChange={(val) => updateSetting('systemBehavior', 'trackRemovalHistory', val)}
              help="Keep detailed history of referrer removals"
            />
            
            <SettingToggle
              label="Notify Referrer on Removal"
              value={settings.systemBehavior.enableReferrerNotificationOnRemoval}
              onChange={(val) => updateSetting('systemBehavior', 'enableReferrerNotificationOnRemoval', val)}
              help="Send notification to referrer when they're removed"
            />
          </div>
        </div>

        {/* Anti-Abuse Protection */}
        <div style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '24px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertTriangle size={24} />
            Anti-Abuse Protection
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SettingToggle
              label="Enable Fraud Detection"
              value={settings.antiAbuse.enableFraudDetection}
              onChange={(val) => updateSetting('antiAbuse', 'enableFraudDetection', val)}
              help="Monitor for suspicious referral patterns"
            />
            
            <SettingInput
              label="Max Referrer Changes Per Year"
              value={settings.antiAbuse.maxReferrerChangesPerYear}
              onChange={(val) => updateSetting('antiAbuse', 'maxReferrerChangesPerYear', val)}
              type="number"
              min={0}
              max={10}
              help="Maximum times a musician can change referrers per year (0 = unlimited)"
            />
            
            <SettingInput
              label="Min Earnings Before Removal ($)"
              value={settings.antiAbuse.minEarningsBeforeRemoval}
              onChange={(val) => updateSetting('antiAbuse', 'minEarningsBeforeRemoval', val)}
              type="number"
              min={0}
              max={1000}
              help="Minimum earnings required before can remove referrer"
            />
            
            <SettingInput
              label="Cooling Off Period (Days)"
              value={settings.antiAbuse.coolingOffPeriodDays}
              onChange={(val) => updateSetting('antiAbuse', 'coolingOffPeriodDays', val)}
              type="number"
              min={0}
              max={90}
              help="Days to wait before accepting new referrer after removal"
            />
            
            <SettingToggle
              label="Flag Suspicious Patterns"
              value={settings.antiAbuse.flagSuspiciousPatterns}
              onChange={(val) => updateSetting('antiAbuse', 'flagSuspiciousPatterns', val)}
              help="Alert admins to suspicious referral behavior"
            />
          </div>
        </div>

        {/* Financial Settings */}
        <div style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '24px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <DollarSign size={24} />
            Financial Settings
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SettingInput
              label="Min Payout Threshold ($)"
              value={settings.financial.minPayoutThreshold}
              onChange={(val) => updateSetting('financial', 'minPayoutThreshold', val)}
              type="number"
              min={1}
              max={100}
              step={0.01}
              help="Minimum amount before referrer receives payment"
            />
            
            <div style={{
              backgroundColor: '#374151',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <p style={{ color: 'white', marginBottom: '12px', fontWeight: 'bold' }}>
                Payout Frequency
              </p>
              <select
                value={settings.financial.payoutFrequency}
                onChange={(e) => updateSetting('financial', 'payoutFrequency', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  backgroundColor: '#1F2937',
                  color: 'white',
                  border: '1px solid #4B5563'
                }}
              >
                <option value="immediate">Immediate</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <p style={{ color: '#9CA3AF', margin: '8px 0 0 0', fontSize: '12px' }}>
                How often referrers receive their earnings
              </p>
            </div>
            
            <SettingInput
              label="Monthly Earnings Cap ($)"
              value={settings.financial.referrerEarningsCapPerMonth}
              onChange={(val) => updateSetting('financial', 'referrerEarningsCapPerMonth', val)}
              type="number"
              min={0}
              max={10000}
              help="Maximum referrer can earn per month (0 = unlimited)"
            />
            
            <SettingToggle
              label="Platform Absorbs Commission"
              value={settings.financial.platformAbsorbsCommission}
              onChange={(val) => updateSetting('financial', 'platformAbsorbsCommission', val)}
              help="True = platform pays commission, False = deducted from musician earnings"
            />
          </div>
        </div>

        {/* Special Programs */}
        <div style={{
          backgroundColor: '#1F2937',
          borderRadius: '12px',
          padding: '24px'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '24px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Calendar size={24} />
            Special Programs
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SettingToggle
              label="Enable Loyalty Bonuses"
              value={settings.specialPrograms.enableLoyaltyBonuses}
              onChange={(val) => updateSetting('specialPrograms', 'enableLoyaltyBonuses', val)}
              help="Give extra commission for long-term referral relationships"
            />
            
            {settings.specialPrograms.enableLoyaltyBonuses && (
              <>
                <SettingInput
                  label="Loyalty Bonus After (Months)"
                  value={settings.specialPrograms.loyaltyBonusAfterMonths}
                  onChange={(val) => updateSetting('specialPrograms', 'loyaltyBonusAfterMonths', val)}
                  type="number"
                  min={6}
                  max={36}
                  help="Months before loyalty bonus kicks in"
                />
                
                <SettingInput
                  label="Loyalty Bonus Percentage"
                  value={settings.specialPrograms.loyaltyBonusPercentage}
                  onChange={(val) => updateSetting('specialPrograms', 'loyaltyBonusPercentage', val)}
                  type="number"
                  min={0.1}
                  max={5}
                  step={0.1}
                  help="Extra percentage for loyalty bonus"
                />
              </>
            )}
            
            <SettingToggle
              label="Enable Seasonal Extensions"
              value={settings.specialPrograms.enableSeasonalExtensions}
              onChange={(val) => updateSetting('specialPrograms', 'enableSeasonalExtensions', val)}
              help="Allow special promotional extensions during holidays"
            />
            
            {settings.specialPrograms.enableSeasonalExtensions && (
              <SettingInput
                label="Seasonal Extension Days"
                value={settings.specialPrograms.seasonalExtensionDays}
                onChange={(val) => updateSetting('specialPrograms', 'seasonalExtensionDays', val)}
                type="number"
                min={30}
                max={180}
                help="Days to add during seasonal promotions"
              />
            )}
          </div>
        </div>

      </div>

      {/* Impact Warning */}
      {hasChanges && (
        <div style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '24px',
          display: 'flex',
          gap: '12px'
        }}>
          <Info size={20} style={{ color: '#D97706', flexShrink: 0 }} />
          <div>
            <p style={{ color: '#92400E', margin: '0 0 4px 0', fontWeight: 'bold' }}>
              Settings Changes Pending
            </p>
            <p style={{ color: '#78350F', margin: 0, fontSize: '14px' }}>
              These changes will affect all future referral relationships. 
              Existing relationships will continue under their original terms unless specifically migrated.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const SettingInput = ({ label, value, onChange, type = 'number', min, max, step, help }) => (
  <div style={{
    backgroundColor: '#374151',
    borderRadius: '8px',
    padding: '16px'
  }}>
    <label style={{ display: 'block' }}>
      <p style={{ color: 'white', margin: '0 0 8px 0', fontWeight: 'bold' }}>
        {label}
      </p>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) : e.target.value)}
        min={min}
        max={max}
        step={step}
        style={{
          width: '100%',
          padding: '8px',
          borderRadius: '4px',
          backgroundColor: '#1F2937',
          color: 'white',
          border: '1px solid #4B5563'
        }}
      />
      {help && (
        <p style={{ color: '#9CA3AF', margin: '8px 0 0 0', fontSize: '12px' }}>
          {help}
        </p>
      )}
    </label>
  </div>
);

const SettingToggle = ({ label, value, onChange, help, icon }) => (
  <div style={{
    backgroundColor: '#374151',
    borderRadius: '8px',
    padding: '16px'
  }}>
    <label style={{ 
      display: 'flex', 
      alignItems: 'start', 
      justifyContent: 'space-between',
      cursor: 'pointer'
    }}>
      <div style={{ flex: 1 }}>
        <p style={{ 
          color: 'white', 
          margin: '0 0 4px 0', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {icon}
          {label}
        </p>
        {help && (
          <p style={{ color: '#9CA3AF', margin: 0, fontSize: '12px' }}>
            {help}
          </p>
        )}
      </div>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        style={{ 
          width: '20px', 
          height: '20px', 
          cursor: 'pointer',
          accentColor: '#7C3AED',
          marginLeft: '12px'
        }}
      />
    </label>
  </div>
);

export default ReferralSystemSettings;